/**
 * Admin History view (PRD §15.2) — browse the audit log.
 *
 * Layout mirrors `/deals` and `/reports`: filter chips above, a
 * `DataTable` below, server-driven pagination at the bottom. Clicking
 * a row opens a side drawer with the full record + a side-by-side
 * before/after diff of the payload.
 *
 * Known constraints reflected in this UI (vs. a wishlist):
 *   * The audit-log API takes filters `entity_type`, `entity_id`,
 *     `actor_id`, `action` only — no date range. Until the foundation
 *     gets a `created_after` / `created_before`, the UI doesn't show
 *     those pickers; filtering by a single entity / actor is the
 *     escape hatch.
 *   * `action` server-side is exact match. We populate the action
 *     suggestions from `/api/audit/actions` (distinct labels seen so
 *     far) and let the user pick one; an unrecognised free-text value
 *     is sent as-is and matches nothing — the empty-state explains.
 *   * The schema has a single `payload` JSONB; by convention update
 *     events use `{ changed: { <field>: { from, to } } }`. The drawer
 *     auto-detects that shape and renders a real diff; other shapes
 *     (create / delete / login / system) fall back to a pretty-printed
 *     JSON block.
 *   * Server enforces admin-only (`require_role(Role.ADMIN)`); the
 *     `beforeLoad` redirect just keeps non-admins from staring at a
 *     spinner before the 403 lands.
 */

import { useQuery } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { Check, Copy, History, Search, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { type Column, DataTable } from "../components/DataTable";
import { type UserListItem, usersApi } from "../lib/api";
import { type AuditLogEntry, auditApi } from "../lib/auditApi";
import { useAuthStore } from "../lib/auth";

export const Route = createFileRoute("/_app/settings/audit")({
  beforeLoad: () => {
    const user = useAuthStore.getState().user;
    if (user && user.role !== "admin") {
      throw redirect({ to: "/deals" });
    }
  },
  component: AuditPage,
});

const PAGE_SIZE = 50;

const ENTITY_TYPES = ["contact", "deal", "account", "followup", "user", "system"] as const;

function AuditPage() {
  const [actionInput, setActionInput] = useState("");
  const [debouncedAction, setDebouncedAction] = useState("");
  const [entityType, setEntityType] = useState("");
  const [entityId, setEntityId] = useState("");
  const [actorId, setActorId] = useState("");
  const [page, setPage] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedAction(actionInput.trim()), 250);
    return () => clearTimeout(t);
  }, [actionInput]);

  const filters = useMemo(
    () => ({
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
      entity_type: entityType || undefined,
      entity_id: entityId.trim() || undefined,
      actor_id: actorId || undefined,
      action: debouncedAction || undefined,
    }),
    [page, entityType, entityId, actorId, debouncedAction],
  );

  const listQuery = useQuery({
    queryKey: ["audit", filters],
    queryFn: () => auditApi.list(filters),
  });

  const actionsQuery = useQuery({
    queryKey: ["audit-actions"],
    queryFn: () => auditApi.actions(),
    staleTime: 60_000,
  });

  const usersQuery = useQuery({
    queryKey: ["users", "active"],
    queryFn: () => usersApi.list({ active_only: true }),
  });
  const usersById = useMemo(() => {
    const m = new Map<string, UserListItem>();
    for (const u of usersQuery.data?.items ?? []) m.set(u.id, u);
    return m;
  }, [usersQuery.data]);

  const data = listQuery.data;
  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;
  const hasFilter = Boolean(debouncedAction || entityType || entityId.trim() || actorId);

  function clearFilters() {
    setActionInput("");
    setDebouncedAction("");
    setEntityType("");
    setEntityId("");
    setActorId("");
    setPage(0);
  }

  const columns: Column<AuditLogEntry>[] = [
    {
      header: "Time",
      width: "w-32",
      cell: (row) => <RelativeTime iso={row.created_at} />,
    },
    {
      header: "Actor",
      width: "w-44",
      cell: (row) => (
        <ActorCell actorId={row.actor_id} role={row.actor_role} usersById={usersById} />
      ),
    },
    {
      header: "Action",
      width: "w-36",
      cell: (row) => (
        <span className="rounded-md bg-black/[0.05] px-2 py-0.5 font-mono text-xs dark:bg-white/[0.08]">
          {row.action}
        </span>
      ),
    },
    {
      header: "Entity",
      width: "w-28",
      cell: (row) => <span className="text-xs opacity-80">{row.entity_type}</span>,
    },
    {
      header: "Entity ID",
      width: "w-44",
      hideOnSmall: true,
      cell: (row) => <CopyableId id={row.entity_id} />,
    },
    {
      header: "",
      width: "w-16",
      align: "right",
      cell: () => (
        <span className="text-xs font-medium text-[var(--accent)] underline-offset-2 hover:underline">
          View
        </span>
      ),
    },
  ];

  return (
    <div className="page-shell space-y-4 p-4 sm:p-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
            <History className="size-5 opacity-70" aria-hidden />
            Audit
          </h1>
          <p className="text-sm opacity-60">
            Append-only record of who did what. Read-only; entries cannot be edited or deleted.
          </p>
        </div>
      </header>

      <section className="space-y-2 rounded-xl border border-[var(--border)] p-3">
        <div className="relative">
          <Search
            className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 opacity-50"
            aria-hidden
          />
          <input
            type="search"
            value={actionInput}
            onChange={(e) => {
              setActionInput(e.target.value);
              setPage(0);
            }}
            placeholder="Filter by action label (e.g. update, login, soft_delete)…"
            list="audit-action-suggestions"
            aria-label="Action label"
            className="w-full rounded-md border border-[var(--border)] bg-transparent py-2 pl-8 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
          />
          <datalist id="audit-action-suggestions">
            {(actionsQuery.data?.items ?? []).map((label) => (
              <option key={label} value={label} />
            ))}
          </datalist>
          {actionInput && (
            <button
              type="button"
              onClick={() => {
                setActionInput("");
                setDebouncedAction("");
                setPage(0);
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 opacity-50 hover:opacity-100"
              aria-label="Clear action filter"
            >
              <X className="size-4" />
            </button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 text-xs">
          <select
            value={entityType}
            onChange={(e) => {
              setEntityType(e.target.value);
              setPage(0);
            }}
            aria-label="Entity type"
            className="rounded-md border border-[var(--border)] bg-transparent px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
          >
            <option value="">Any entity type</option>
            {ENTITY_TYPES.map((t) => (
              <option key={t} value={t}>
                Entity: {t}
              </option>
            ))}
          </select>

          <select
            value={actorId}
            onChange={(e) => {
              setActorId(e.target.value);
              setPage(0);
            }}
            aria-label="Actor"
            className="rounded-md border border-[var(--border)] bg-transparent px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
          >
            <option value="">Any actor</option>
            {usersQuery.data?.items.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name || u.email} ({u.role})
              </option>
            ))}
          </select>

          <input
            type="text"
            value={entityId}
            onChange={(e) => {
              setEntityId(e.target.value);
              setPage(0);
            }}
            placeholder="Entity ID (UUID)"
            aria-label="Entity ID"
            className="w-full max-w-full rounded-md border border-[var(--border)] bg-transparent px-2 py-1 font-mono text-xs focus:outline-none focus:ring-2 focus:ring-[var(--accent)] sm:w-64"
          />

          {hasFilter && (
            <button
              type="button"
              onClick={clearFilters}
              className="rounded-md px-2 py-1 text-xs underline opacity-70 hover:opacity-100 sm:ml-auto"
            >
              Clear filters
            </button>
          )}
        </div>
      </section>

      {listQuery.isLoading ? (
        <p className="text-sm opacity-60">Loading…</p>
      ) : listQuery.error ? (
        <div
          role="alert"
          className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300"
        >
          Couldn't load audit entries: {(listQuery.error as Error).message}
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between text-xs opacity-60">
            <span>
              {data?.total ?? 0} {data?.total === 1 ? "entry" : "entries"}
              {hasFilter ? " match your filters" : ""}
            </span>
          </div>
          <DataTable
            columns={columns}
            rows={data?.items ?? []}
            rowKey={(r) => r.id}
            onRowClick={(r) => setSelectedId(r.id)}
            empty={<EmptyState hasFilter={hasFilter} />}
          />
          <Pager
            page={page}
            totalPages={totalPages}
            total={data?.total ?? 0}
            shown={data?.items.length ?? 0}
            onPrev={() => setPage((p) => Math.max(0, p - 1))}
            onNext={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
          />
        </>
      )}

      {selectedId && (
        <AuditDetailDrawer
          id={selectedId}
          usersById={usersById}
          onClose={() => setSelectedId(null)}
        />
      )}
    </div>
  );
}

// ── Cells ───────────────────────────────────────────────────────────────────

function RelativeTime({ iso }: { iso: string }) {
  const date = new Date(iso);
  const ago = formatRelative(date);
  return (
    <time dateTime={iso} title={date.toISOString()} className="text-xs">
      {ago}
    </time>
  );
}

function formatRelative(date: Date): string {
  const now = Date.now();
  const diffMs = now - date.getTime();
  const sec = Math.round(diffMs / 1000);
  if (sec < 60) return `${Math.max(sec, 0)}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 30) return `${day}d ago`;
  return date.toLocaleDateString();
}

function ActorCell({
  actorId,
  role,
  usersById,
}: {
  actorId: string | null;
  role: string;
  usersById: Map<string, UserListItem>;
}) {
  if (!actorId) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs">
        <span className="opacity-70">system</span>
      </span>
    );
  }
  const user = usersById.get(actorId);
  return (
    <div className="min-w-0 text-xs">
      <div className="truncate">{user ? user.name || user.email : `${actorId.slice(0, 8)}…`}</div>
      <div className="truncate opacity-60">{role}</div>
    </div>
  );
}

function CopyableId({ id }: { id: string | null }) {
  const [copied, setCopied] = useState(false);
  if (!id) return <span className="text-xs opacity-50">—</span>;
  const onClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (navigator.clipboard) {
      void navigator.clipboard.writeText(id).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      });
    }
  };
  return (
    <button
      type="button"
      onClick={onClick}
      title={`Click to copy: ${id}`}
      className="group inline-flex items-center gap-1 rounded px-1 font-mono text-xs opacity-80 hover:bg-black/[0.05] hover:opacity-100 dark:hover:bg-white/[0.08]"
    >
      <span className="truncate">{id.slice(0, 8)}…</span>
      {copied ? (
        <Check className="size-3 text-green-600 dark:text-green-400" aria-hidden />
      ) : (
        <Copy className="size-3 opacity-50 group-hover:opacity-100" aria-hidden />
      )}
    </button>
  );
}

function EmptyState({ hasFilter }: { hasFilter: boolean }) {
  if (hasFilter) {
    return (
      <div className="rounded-xl border border-dashed border-[var(--border)] p-8 text-center">
        <Search className="mx-auto mb-3 size-7 opacity-40" aria-hidden />
        <p className="text-sm font-medium">No matches</p>
        <p className="mt-1 text-sm opacity-60">Try a different filter or clear them.</p>
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-dashed border-[var(--border)] p-10 text-center">
      <History className="mx-auto mb-3 size-8 opacity-40" aria-hidden />
      <p className="text-sm font-medium">No audit entries yet</p>
      <p className="mt-1 text-sm opacity-60">
        As routers are wired into <code className="font-mono text-xs">audit.record(...)</code>,
        events will appear here.
      </p>
    </div>
  );
}

function Pager({
  page,
  totalPages,
  total,
  shown,
  onPrev,
  onNext,
}: {
  page: number;
  totalPages: number;
  total: number;
  shown: number;
  onPrev: () => void;
  onNext: () => void;
}) {
  if (total <= PAGE_SIZE) return null;
  return (
    <div className="flex items-center justify-between text-xs opacity-70">
      <span>
        Page {page + 1} of {totalPages} · {shown} shown
      </span>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onPrev}
          disabled={page === 0}
          className="rounded-md border border-[var(--border)] px-3 py-1 disabled:opacity-40"
        >
          Prev
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={page >= totalPages - 1}
          className="rounded-md border border-[var(--border)] px-3 py-1 disabled:opacity-40"
        >
          Next
        </button>
      </div>
    </div>
  );
}

// ── Detail drawer ───────────────────────────────────────────────────────────

function AuditDetailDrawer({
  id,
  usersById,
  onClose,
}: {
  id: string;
  usersById: Map<string, UserListItem>;
  onClose: () => void;
}) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["audit", id],
    queryFn: () => auditApi.get(id),
  });

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    // Native <dialog open> gives us the right ARIA semantics
    // (role=dialog, aria-modal) for free; we drive open/close
    // imperatively (mount/unmount) rather than via showModal() so the
    // backdrop is part of the same flex layout instead of the UA's
    // fullscreen ::backdrop pseudo. `open` is set so the dialog
    // actually renders (omitting it hides the element entirely).
    <dialog
      open
      aria-label="Audit entry details"
      className="fixed inset-0 z-40 m-0 flex h-full max-h-none w-full max-w-none border-0 bg-transparent p-0 text-[var(--fg)]"
    >
      <button
        type="button"
        aria-label="Close drawer"
        className="flex-1 cursor-default bg-black/30"
        onClick={onClose}
      />
      <aside className="flex h-full w-full max-w-2xl flex-col overflow-hidden border-l border-[var(--border)] bg-[var(--bg)] shadow-2xl">
        <header className="flex items-start justify-between gap-3 border-b border-[var(--border)] px-5 py-4">
          <div className="min-w-0 space-y-1">
            <h2 className="text-base font-semibold tracking-tight">Audit entry</h2>
            <p className="truncate font-mono text-xs opacity-60">{id}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded p-1 opacity-70 hover:bg-black/5 hover:opacity-100 dark:hover:bg-white/10"
          >
            <X className="size-5" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {isLoading ? (
            <p className="text-sm opacity-60">Loading…</p>
          ) : error ? (
            <div
              role="alert"
              className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300"
            >
              Couldn't load entry: {(error as Error).message}
            </div>
          ) : data ? (
            <DrawerBody entry={data} usersById={usersById} />
          ) : null}
        </div>
      </aside>
    </dialog>
  );
}

function DrawerBody({
  entry,
  usersById,
}: {
  entry: AuditLogEntry;
  usersById: Map<string, UserListItem>;
}) {
  const actor = entry.actor_id ? usersById.get(entry.actor_id) : undefined;
  const created = new Date(entry.created_at);
  return (
    <div className="space-y-5 break-words">
      <dl className="grid grid-cols-[5rem_minmax(0,1fr)] gap-x-3 gap-y-2 text-xs sm:grid-cols-[7rem_minmax(0,1fr)]">
        <Field label="When">
          <div className="space-y-0.5">
            <div className="font-medium">{created.toLocaleString()}</div>
            <div className="opacity-60">{entry.created_at}</div>
          </div>
        </Field>
        <Field label="Actor">
          {entry.actor_id ? (
            <div className="space-y-0.5">
              <div className="font-medium">
                {actor ? actor.name || actor.email : `User ${entry.actor_id.slice(0, 8)}…`}
              </div>
              <div className="opacity-60">{entry.actor_role}</div>
              <div className="font-mono opacity-60">{entry.actor_id}</div>
            </div>
          ) : (
            <span className="opacity-70">system (no human actor)</span>
          )}
        </Field>
        <Field label="Action">
          <span className="rounded-md bg-black/[0.05] px-2 py-0.5 font-mono text-xs dark:bg-white/[0.08]">
            {entry.action}
          </span>
        </Field>
        <Field label="Entity">
          <div className="space-y-0.5">
            <div>{entry.entity_type}</div>
            {entry.entity_id ? (
              <div className="font-mono opacity-60">{entry.entity_id}</div>
            ) : (
              <div className="opacity-50">— (no specific entity)</div>
            )}
          </div>
        </Field>
        {entry.request_id && (
          <Field label="Request ID">
            <span className="font-mono opacity-80">{entry.request_id}</span>
          </Field>
        )}
      </dl>

      <section>
        <h3 className="mb-2 text-xs font-medium uppercase tracking-wide opacity-60">Payload</h3>
        <PayloadView payload={entry.payload} />
      </section>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="text-xs uppercase tracking-wide opacity-50">{label}</dt>
      <dd>{children}</dd>
    </>
  );
}

// ── Diff renderer ───────────────────────────────────────────────────────────

interface ChangedField {
  field: string;
  from: unknown;
  to: unknown;
}

/**
 * Detect the convention `payload.changed = { <field>: { from, to } }`
 * (see app/services/audit.py docstring + tests). When the shape
 * matches, return the parsed change list; otherwise null and the
 * fallback "raw JSON" view kicks in.
 */
function extractChanges(payload: Record<string, unknown> | null): ChangedField[] | null {
  if (!payload || typeof payload !== "object") return null;
  const changed = payload.changed;
  if (!changed || typeof changed !== "object" || Array.isArray(changed)) return null;
  const out: ChangedField[] = [];
  for (const [field, value] of Object.entries(changed as Record<string, unknown>)) {
    if (
      value &&
      typeof value === "object" &&
      !Array.isArray(value) &&
      "from" in value &&
      "to" in value
    ) {
      const v = value as { from: unknown; to: unknown };
      out.push({ field, from: v.from, to: v.to });
    }
  }
  return out.length > 0 ? out : null;
}

function PayloadView({ payload }: { payload: Record<string, unknown> | null }) {
  if (payload === null || payload === undefined) {
    return (
      <p className="rounded-md border border-dashed border-[var(--border)] px-3 py-4 text-center text-xs opacity-60">
        No payload recorded for this event (typical for create / delete / login events).
      </p>
    );
  }
  const changes = extractChanges(payload);
  // Other top-level keys that aren't part of the diff. We render them
  // below the diff as supplementary context (e.g. `scope: "contact"`).
  const extras = Object.fromEntries(Object.entries(payload).filter(([k]) => k !== "changed"));
  const hasExtras = Object.keys(extras).length > 0;

  if (!changes) {
    // Unknown shape — render raw JSON so the admin can still read it.
    return <RawJson value={payload} />;
  }

  return (
    <div className="space-y-4">
      <div className="overflow-hidden rounded-lg border border-[var(--border)]">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-[var(--border)] bg-black/[0.02] text-left uppercase tracking-wide opacity-60 dark:bg-white/[0.03]">
              <th className="px-3 py-2 font-medium">Field</th>
              <th className="px-3 py-2 font-medium">Before</th>
              <th className="px-3 py-2 font-medium">After</th>
            </tr>
          </thead>
          <tbody>
            {changes.map((c) => (
              <tr key={c.field} className="border-b border-[var(--border)] last:border-0 align-top">
                <td className="px-3 py-2 font-mono text-xs">{c.field}</td>
                <td className="bg-red-500/[0.06] px-3 py-2 dark:bg-red-500/[0.1]">
                  <DiffValue value={c.from} side="from" />
                </td>
                <td className="bg-emerald-500/[0.07] px-3 py-2 dark:bg-emerald-500/[0.1]">
                  <DiffValue value={c.to} side="to" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {hasExtras && (
        <div>
          <h4 className="mb-1 text-xs uppercase tracking-wide opacity-50">Context</h4>
          <RawJson value={extras} />
        </div>
      )}
    </div>
  );
}

function DiffValue({ value, side }: { value: unknown; side: "from" | "to" }) {
  if (value === null) {
    return (
      <span className="font-mono text-xs italic opacity-60">{side === "from" ? "—" : "null"}</span>
    );
  }
  if (value === undefined) {
    return <span className="font-mono text-xs italic opacity-60">—</span>;
  }
  if (typeof value === "string") {
    return <span className="break-words font-mono text-xs">{JSON.stringify(value)}</span>;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return <span className="font-mono text-xs">{String(value)}</span>;
  }
  // Object / array — pretty-print but compact.
  return (
    <pre className="whitespace-pre-wrap break-words font-mono text-[11px] leading-snug">
      {JSON.stringify(value, null, 2)}
    </pre>
  );
}

function RawJson({ value }: { value: unknown }) {
  let text: string;
  try {
    text = JSON.stringify(value, null, 2);
  } catch {
    text = String(value);
  }
  return (
    <pre className="overflow-x-auto rounded-md border border-[var(--border)] bg-black/[0.03] p-3 font-mono text-xs leading-snug dark:bg-white/[0.03]">
      {text}
    </pre>
  );
}
